import {
  Button,
  CallbackExtraFieldsType,
  DesignLanguage,
  Grid,
  InputEmailPhone,
  InputOnChange,
  Text,
  useIsDesignLanguage,
  usePhoneNumber,
} from '@afterpaytouch/core'
import { unwrapResult } from '@reduxjs/toolkit'
import React, { FormEvent, FunctionComponent, useEffect, useRef, useState } from 'react'
import { ErrorCode } from '@afterpaytouch/portal-api/types'
import { isPortalApiError } from '@afterpaytouch/portal-api/utils'
import { TFunction } from 'next-i18next'
import { DisabledModal } from '../DisabledModal'
import { Form } from '../Form'
import {
  CheckoutError,
  duplicateErrorCodeList,
  HandleUserLookupResult,
  isCheckoutErrorType,
  isGenericApiError,
  isPublicResponse,
  isUserLookupResponseType,
  RegistrationStatus,
  TerminalErrorCode,
} from '../../model'
import { TrackingEvent } from '../../model/monitoring'
import {
  checkCrossBorder,
  handleEmailLookup,
  mobileLookup,
  useAllowedQuery,
  useAppDispatch,
  useAppRouter,
  useCheckoutConsumerPhoneNumber,
  useCheckoutData,
  useCheckoutError,
  useConsumerSelector,
  useCountry,
  useIsAppleRetailOrder,
  useMobileLogin,
  useRegistration,
  useSupportedCountries,
  useRollout,
  setCohort,
  useLoginIdentityError,
  useMerchantId,
  setLoginIdentityError,
  PublicResponseWithSource,
  useIsApplePaylater,
  useOrderSource,
  useSetCashCohortLookupData,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { isValidEmail } from '../../utils/validation'
import { useXbtBrowserRedirect } from '../../utils/redirects'
import { Cohort, Country } from '@afterpaytouch/portal-api'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { getDefaultIdentityValue, isSupportedLocale } from './utils'
import { useTranslation } from '../../utils/convergence'
import { deriveHasCashtag, evaluateCohort } from '../../state/convergence/helper'
import { getAnonymousErrorPayload } from '../../utils/logging'

type Translate = TFunction
type TranslationSelector = (translate: Translate) => string

const countryNameSelectors: Record<Country, TranslationSelector> = {
  [Country.AU]: (translate) => translate('common:countryName_AU'),
  [Country.US]: (translate) => translate('common:countryName_US'),
  [Country.GB]: (translate) => translate('common:countryName_GB'),
  [Country.NZ]: (translate) => translate('common:countryName_NZ'),
  [Country.CA]: (translate) => translate('common:countryName_CA'),
  [Country.FR]: (translate) => translate('common:countryName_FR'),
  [Country.ES]: (translate) => translate('common:countryName_ES'),
  [Country.IT]: (translate) => translate('common:countryName_IT'),
}

const loginIdentityErrorSelectors: Partial<Record<ErrorCode, TranslationSelector>> = {
  [ErrorCode.InvalidEmailAddress]: (translate) => translate('login:error:emailNotValid'),
  [ErrorCode.RegistrationNotPermitted]: (translate) => translate('login:error:registrationNotPermitted'),
}

const getCountryName = (country: Country, translate: Translate): string => {
  return countryNameSelectors[country](translate)
}

const getLoginIdentityErrorText = (errorCode: ErrorCode | undefined, translate: Translate): string => {
  const selector = errorCode !== undefined ? loginIdentityErrorSelectors[errorCode] : undefined

  if (selector) {
    return selector(translate)
  }

  return translate('login:error:unknown')
}

export type OnSuccessHandler = (
  result: HandleUserLookupResult,
  isMobileLoginMode: boolean,
  email?: string | undefined,
  mobile?: string | undefined,
  hasNoExistingLoginIdentity?: boolean
) => Promise<void>

interface LoginIdentityProps {
  onSuccess: OnSuccessHandler
  onError: (error: CheckoutError | PublicResponseWithSource) => void
  skipCrossRegionCheck?: boolean
}

export const LoginIdentity: FunctionComponent<LoginIdentityProps> = ({ onSuccess, onError, skipCrossRegionCheck = false }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { locale } = useAppRouter()
  const [error, setError] = useState(false)
  const loginIdentityError = useLoginIdentityError()
  const [errorText, setErrorText] = useState<string | React.ReactNode>(loginIdentityError ?? '')
  const [isPhoneNumberValid, setPhoneNumberValid] = useState(true)
  const [isPrefilledEmailChanged, setIsPrefilledEmailChanged] = useState(false)
  const [lock, setLocked] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { buttonSize, textSize } = useCheckoutSizes()
  const checkoutError = useCheckoutError()
  const merchantId = useMerchantId()
  const orderSource = useOrderSource()
  const country = useCountry()
  const supportedCountries = useSupportedCountries()
  const xbtBrowserRedirect = useXbtBrowserRedirect()
  const { skipPrefill: skipPrefillInUrl } = useAllowedQuery()
  const { parseToE164NumberWithoutPlusPrefix } = usePhoneNumber({ country })
  const convergenceRollout = useRollout()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isAppleRetailOrder = useIsAppleRetailOrder()
  const checkout = useCheckoutData()
  const { email, mobile } = useConsumerSelector()
  const mobileFromMerchant = useCheckoutConsumerPhoneNumber()
  const { status: registrationStatus, error: registrationError, isUseExistingMobile } = useRegistration()
  const isDuplicatePhoneNumberError =
    registrationStatus === RegistrationStatus.Error && registrationError?.error !== undefined && duplicateErrorCodeList.includes(registrationError?.error)
  const defaultEmail = isDuplicatePhoneNumberError ? undefined : email ?? checkout?.consumer?.email ?? ''

  const { isMobileLogin: isMobileLoggedIn, mobile: lastMobile } = useMobileLogin()
  const isApplePaylaterFlow = useIsApplePaylater()
  const setCashCohortLookupData = useSetCashCohortLookupData()

  const result = getDefaultIdentityValue({
    initMobileLogin: isUseExistingMobile || isMobileLoggedIn,
    mobile,
    lastMobile,
    mobileFromMerchant,
    email: defaultEmail,
    skipPrefill: skipPrefillInUrl,
  })
  const defaultIdentityValue = isApplePaylaterFlow ? '' : result.defaultIdentityValue
  const initMobileLoginState = isApplePaylaterFlow ? false : result.initMobileLoginState

  const [userIdentityValue, setUserIdentityValue] = useState(defaultIdentityValue ?? '')
  const [isMobileLogin, setMobileLogin] = useState(initMobileLoginState)

  const allowedCountries = supportedCountries.map((supportedCountry) => {
    return {
      countryCode: supportedCountry,
      name: getCountryName(supportedCountry, t),
    }
  })

  const setEmptyErrorText = (): void => {
    setErrorText('')
    setError(false)
  }

  const onCountryChange = async (country: Country): Promise<void> => {
    setEmptyErrorText()
    logEvent(TrackingEvent.PRESSED_CHANGE_COUNTRY_BUTTON_LOGIN_EMAIL_PAGE, { countryCode: country })
    const crossRegionCheck = await dispatch(checkCrossBorder(country))
    if (crossRegionCheck !== null && 'crossRegionCheckoutUrl' in crossRegionCheck) {
      logEvent(TrackingEvent.REDIRECT_CROSS_REGION)
      xbtBrowserRedirect(crossRegionCheck.crossRegionCheckoutUrl, true)
    }
  }

  const onEmailPhoneChange = (e: InputOnChange, { isMobileInputMode, isMobileNumberValid, rawValue = '' }: CallbackExtraFieldsType): void => {
    const value: string = e.target.value ?? ''

    if (value !== '') {
      setEmptyErrorText()
    }

    setMobileLogin(isMobileInputMode)
    setUserIdentityValue(rawValue)
    setPhoneNumberValid(isMobileInputMode ? isMobileNumberValid : false)
  }

  const validateForm = (): boolean => {
    if (isMobileLogin && isPhoneNumberValid) {
      return true
    } else if (!isMobileLogin && isValidEmail(userIdentityValue)) {
      return true
    }

    if (userIdentityValue === '') {
      setErrorText(t('login:error:emailOrMobileIsRequired'))
    } else {
      setErrorText(isMobileLogin ? t('login:error:mobileNotValid') : t('login:error:emailNotValid'))
    }
    inputRef.current?.focus()
    return false
  }

  const handleSignUpFromFRITESError = (): void => onError({ error: TerminalErrorCode.BlockSignUpFromFRITES })

  const doEmailLookup = async (): Promise<void> => {
    try {
      const result = await dispatch(handleEmailLookup(userIdentityValue, false, skipCrossRegionCheck))
      if (isCheckoutErrorType(result) && result.error === TerminalErrorCode.UnsupportedMerchantTradingCountry) {
        logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: ErrorCode.UnsupportedMerchantTradingCountry })
        setErrorText(t('login:error:unsupportedMerchantTradingCountry'))
        setLocked(false)
      } else if (isCheckoutErrorType(result)) {
        onError(result)
      } else if (isUserLookupResponseType(result) && !result.existing) {
        const blockSignUpFromFRITES = !isSupportedLocale(locale)
        const { existing, shadowAccountExists } = result
        const { cohort, theme } = evaluateCohort(
          deriveHasCashtag(result),
          existing,
          convergenceRollout,
          userIdentityValue,
          shadowAccountExists,
          merchantId?.toString(),
          country,
          orderSource
        )
        logEvent(TrackingEvent.ACCOUNT_LOOKUP_ATTEMPTED, {
          customerCohort: cohort,
          checkoutBrand: theme,
          shadowAccountExists,
          convergenceRollout,
          existing,
          lookupType: 'interactive',
          lookupBy: 'email',
        })
        if (blockSignUpFromFRITES) {
          dispatch(setCohort(cohort))
          return handleSignUpFromFRITESError()
        }
        setCashCohortLookupData({
          cohort,
          cashCreationDate: result.creationDate,
          shadowAccountExists: result.shadowAccountExists,
          cashLoginType: result.loginType,
        })

        onSuccess(result, isMobileLogin, userIdentityValue, undefined, true)
      } else {
        logEvent(TrackingEvent.ACCOUNT_LOOKUP_ATTEMPTED, {
          crossRegionCheckoutUrl: 'crossRegionCheckoutUrl' in result ? result.crossRegionCheckoutUrl : undefined,
          isFromInitialiseCheckout: 'isFromInitialiseCheckout' in result ? result.isFromInitialiseCheckout : undefined,
          passwordExists: 'passwordExists' in result ? result.passwordExists : undefined,
          existing: 'existing' in result ? result.existing : undefined,
          shadowAccountExists: 'shadowAccountExists' in result ? result.shadowAccountExists : undefined,
          convergenceRollout,
          lookupType: 'interactive',
        })
        onSuccess(result, isMobileLogin, userIdentityValue)
      }
    } catch (e) {
      let code: ErrorCode | undefined

      if (isPortalApiError(e)) {
        code = e.errorCode

        // Handle Cash fraud decline - redirect to terminal error
        // This only happens for Cash customers who fail fraud checks
        if (code === ErrorCode.UnableToProcessOrder) {
          // Set CashOnly cohort to ensure Cash-branded error messages
          dispatch(setCohort(Cohort.CashOnly))
          onError({ error: TerminalErrorCode.RiskDeclined })
          return
        }

        logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: code })
      } else if (isGenericApiError(e)) {
        code = ErrorCode.Error
        logEvent(TrackingEvent.VIEWED_ANONYMOUS_USER_LOGIN_ERROR, getAnonymousErrorPayload(e))
      }

      setErrorText(getLoginIdentityErrorText(code, t))
    }
  }

  const doMobileLogin = async (): Promise<void> => {
    try {
      const formattedMobile = parseToE164NumberWithoutPlusPrefix(userIdentityValue)
      const res = unwrapResult(
        await dispatch(
          mobileLookup({
            mobile: formattedMobile,
            isFromInitialiseCheckout: false,
          })
        )
      )

      if (isPublicResponse(res)) {
        // if we've picked up a PublicResponse it means there was a failure talking to portal-api via verify
        onError({ ...res, source: res.source })
        return // throw terminal error here
      }

      const { existing, shadowAccountExists, creationDate, loginType } = res
      const { cohort, theme } = evaluateCohort(
        deriveHasCashtag(res),
        existing,
        convergenceRollout,
        undefined,
        shadowAccountExists,
        merchantId?.toString(),
        country,
        orderSource
      )
      setCashCohortLookupData({
        cohort,
        cashCreationDate: creationDate,
        shadowAccountExists,
        cashLoginType: loginType,
      })

      logEvent(TrackingEvent.ACCOUNT_LOOKUP_ATTEMPTED, {
        customerCohort: cohort,
        checkoutBrand: theme,
        shadowAccountExists,
        convergenceRollout,
        existing,
        lookupType: 'interactive',
        lookupBy: 'mobile',
      })
      const blockSignUpFromFRITES = !isSupportedLocale(locale) && !res.existing
      if (blockSignUpFromFRITES) {
        return handleSignUpFromFRITESError()
      }
      if ('error' in res) {
        setErrorText(t('login:error:mobileNotValid'))
        // @ts-expect-error TODO: fix type error
        onError(res)
      } else {
        onSuccess(res, isMobileLogin, undefined, formattedMobile)
      }
    } catch {
      setErrorText(t('login:error:mobileNotValid'))
      setPhoneNumberValid(false)
    }
  }

  const onFormSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    logEvent(TrackingEvent.PRESSED_LOGIN_EMAIL_CONTINUE)

    const isValid = validateForm()
    if (!isValid) {
      return
    }
    setLocked(true)
    await (isMobileLogin ? doMobileLogin() : doEmailLookup())
    setLocked(false)
  }

  const onEmailPhoneBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    { isMobileNumberValid, isMobileInputMode, rawValue = '' }: CallbackExtraFieldsType
  ): void => {
    setMobileLogin(isMobileInputMode)
    setUserIdentityValue(rawValue)
    setPhoneNumberValid(isMobileInputMode ? isMobileNumberValid : false)
    setError(isMobileInputMode ? !isMobileNumberValid : false)

    if (e.target.value !== '') {
      dispatch(setLoginIdentityError(''))
    }
  }

  useEffect(() => {
    if (checkoutError?.error === TerminalErrorCode.UnsupportedMerchantTradingCountry) {
      logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: ErrorCode.UnsupportedMerchantTradingCountry })
      setErrorText(t('login:error:unsupportedMerchantTradingCountry'))
    }
  }, [checkoutError])

  useEffect(() => {
    if (userIdentityValue !== defaultIdentityValue && !isPrefilledEmailChanged) {
      setIsPrefilledEmailChanged(true)
      logEvent(TrackingEvent.CHANGED_PREFILLED_EMAIL_OR_MOBILE, { loginType: isMobileLogin ? 'mobile' : 'email' })
    }
  }, [userIdentityValue])

  return (
    <>
      <Form onSubmit={onFormSubmit} testNameSpace='login-identity'>
        <Grid>
          <Text size={textSize} id='loginEmailLabel'>
            {isBronteDesignLanguage && t('login:enterEmailOrMobile')}
          </Text>
        </Grid>
        <Grid>
          <MaskedElement stylingModifier='wrap' actionName='Mobile/Email Input'>
            <InputEmailPhone
              ref={inputRef}
              enableInputPhoneMode={true}
              label={isAppleRetailOrder ? t('login:mobileNumberOrEmail') : t('login:emailOrMobileNumber')}
              onChange={onEmailPhoneChange}
              onBlur={onEmailPhoneBlur}
              name='identifier'
              testNameSpace='login-identity'
              required={true}
              autoComplete='username'
              defaultValue={defaultIdentityValue}
              error={error || Boolean(errorText)}
              errorText={errorText}
              disabled={lock}
              aria-labelledby='loginEmailLabel'
              autoFocus={true}
              autoSelectOnFocus={true}
              selectedCountry={country}
              allowedCountries={allowedCountries.length > 0 ? allowedCountries : undefined}
              onCountryChange={onCountryChange}
            />
          </MaskedElement>
        </Grid>
        <Grid>
          <Grid.Item>
            <Button.Primary loading={lock} size={buttonSize} type='submit' testNameSpace='login-identity' disabled={lock} padding='Fluid'>
              {t('common:continue')}
            </Button.Primary>
          </Grid.Item>
        </Grid>
      </Form>
      {lock && <DisabledModal />}
    </>
  )
}
